import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Ticket, AlertTriangle, Clock, MessageSquare, UserCheck } from "lucide-react";

const CATEGORIES = ["IT Support","HR Support","Facility","Finance"];
const PRIORITIES = ["Low","Normal","High","Urgent"];
const STATUSES = ["Open","In Progress","Waiting","Resolved","Closed"];
const STATUS_STYLE = {
  Open: "bg-red-50 text-red-700",
  "In Progress": "bg-orange-50 text-orange-700",
  Waiting: "bg-slate-100 text-slate-700",
  Resolved: "bg-emerald-50 text-emerald-700",
  Closed: "bg-slate-100 text-slate-500",
};
const PRIORITY_STYLE = {
  Urgent: "bg-red-50 text-red-700",
  High: "bg-orange-50 text-orange-700",
  Normal: "bg-slate-100 text-slate-700",
  Low: "bg-slate-100 text-slate-500",
};

function fmtRel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}
function slaState(t) {
  if (["Resolved", "Closed"].includes(t.status)) return { label: "Met", tone: "text-emerald-600" };
  if (!t.target_at) return { label: "—", tone: "text-slate-500" };
  const now = new Date();
  const target = new Date(t.target_at);
  const diff = (target - now) / 1000 / 60; // minutes
  if (diff < 0) return { label: `Breached ${Math.floor(-diff/60)}h ago`, tone: "text-red-600 font-semibold" };
  if (diff < 60) return { label: `${Math.floor(diff)}m left`, tone: "text-orange-600 font-semibold" };
  return { label: `${Math.floor(diff/60)}h left`, tone: "text-slate-600" };
}

export default function Helpdesk() {
  const { user } = useAuth();
  const isReviewer = user?.role === "admin" || user?.role === "hr";
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ title:"", description:"", category:"IT Support", priority:"Normal" });
  const [openTicket, setOpenTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [assignEmp, setAssignEmp] = useState("");

  const load = async () => {
    if (user?.employee_id) setMine((await api.get("/helpdesk/tickets/mine")).data);
    if (isReviewer) {
      const [a, s, e] = await Promise.all([api.get("/helpdesk/tickets"), api.get("/helpdesk/summary"), api.get("/employees")]);
      setAll(a.data); setSummary(s.data); setEmployees(e.data);
    }
  };
  useEffect(() => { load(); }, [user?.employee_id, isReviewer]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/helpdesk/tickets", form);
      toast.success("Ticket raised");
      setNewOpen(false);
      setForm({ title:"", description:"", category:"IT Support", priority:"Normal" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const assign = async () => {
    if (!assignEmp || !openTicket) return;
    try {
      const { data } = await api.put(`/helpdesk/tickets/${openTicket.id}/assign`, { assignee_id: assignEmp });
      toast.success(`Assigned to ${data.assignee_name}`);
      setOpenTicket(data); setAssignEmp("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (status) => {
    if (!openTicket) return;
    try {
      const { data } = await api.put(`/helpdesk/tickets/${openTicket.id}/status`, { status });
      toast.success(`Status → ${status}`);
      setOpenTicket(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const postReply = async () => {
    if (!openTicket || !reply.trim()) return;
    try {
      const { data } = await api.post(`/helpdesk/tickets/${openTicket.id}/reply`, { body: reply });
      setOpenTicket(data); setReply("");
      toast.success("Reply posted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="helpdesk-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">Helpdesk</div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Raise it. Route it. Resolve it.</h1>
          <p className="mt-1 text-sm text-slate-500">IT & HR tickets with SLA timers and clean assignment routing.</p>
        </div>
        <Button onClick={() => setNewOpen(true)} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="new-ticket-btn">
          <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Raise ticket
        </Button>
      </div>

      {isReviewer && summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total" value={summary.total} icon={Ticket} tone="red" />
          <StatCard label="Open + In Progress" value={(summary.by_status?.Open||0)+(summary.by_status?.["In Progress"]||0)} icon={Clock} tone="orange" />
          <StatCard label="SLA breached" value={summary.breached} icon={AlertTriangle} tone={summary.breached ? "red" : "green"} />
          <StatCard label="Resolved" value={summary.by_status?.Resolved||0} icon={UserCheck} tone="green" />
        </div>
      )}

      <Tabs defaultValue={isReviewer ? "all" : "mine"}>
        <TabsList>
          {user?.employee_id && <TabsTrigger value="mine" data-testid="tab-my-tickets">My tickets</TabsTrigger>}
          {isReviewer && <TabsTrigger value="all" data-testid="tab-all-tickets">All tickets</TabsTrigger>}
          {isReviewer && <TabsTrigger value="rules" data-testid="tab-rules">Auto-Assign Rules</TabsTrigger>}
        </TabsList>

        {user?.employee_id && (
          <TabsContent value="mine"><TicketList tickets={mine} onOpen={setOpenTicket} /></TabsContent>
        )}
        {isReviewer && (
          <TabsContent value="all"><TicketList tickets={all} showRaiser onOpen={setOpenTicket} /></TabsContent>
        )}
        {isReviewer && (
          <TabsContent value="rules"><AutoAssignRules employees={employees} /></TabsContent>
        )}
      </Tabs>

      {/* Create ticket */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Raise a ticket</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <FormField label="Title"><Input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="ticket-title" /></FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Category">
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </FormField>
              <FormField label="Priority">
                <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                  <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </FormField>
            </div>
            <FormField label="Describe the issue"><Textarea rows={4} required value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="ticket-desc" /></FormField>
            <div className="text-xs text-slate-500">
              SLA target: <span className="font-semibold">{form.priority === "Urgent" ? "4h" : form.priority === "High" ? "12h" : form.priority === "Normal" ? "24h" : "72h"}</span> from now.
            </div>
            <div className="flex justify-end"><Button type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="ticket-submit">Raise</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ticket detail */}
      <Dialog open={!!openTicket} onOpenChange={(o) => !o && setOpenTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {openTicket && (
            <>
              <DialogHeader>
                <DialogTitle>
                  <span className="font-mono text-slate-500 mr-2">{openTicket.ticket_no}</span>{openTicket.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Badge className={`border-0 ${STATUS_STYLE[openTicket.status]}`}>{openTicket.status}</Badge>
                  <Badge className={`border-0 ${PRIORITY_STYLE[openTicket.priority]}`}>{openTicket.priority}</Badge>
                  <Badge className="bg-slate-100 text-slate-700 border-0">{openTicket.category}</Badge>
                  <span className={`text-xs ${slaState(openTicket).tone} ml-auto flex items-center gap-1`}>
                    <Clock className="h-3 w-3" strokeWidth={2} />{slaState(openTicket).label}
                  </span>
                </div>
                <div className="text-xs text-slate-500">
                  Raised by <b>{openTicket.raised_by_name}</b> · {fmtRel(openTicket.created_at)} · Assigned to <b>{openTicket.assignee_name || "unassigned"}</b>
                </div>
                <Card className="p-4 rounded-lg border-slate-200 bg-slate-50">
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">{openTicket.description}</div>
                </Card>

                {isReviewer && (
                  <div className="flex flex-wrap gap-2 items-center">
                    <Label className="text-xs">Assign to</Label>
                    <Select value={assignEmp} onValueChange={setAssignEmp}>
                      <SelectTrigger className="w-56 h-9" data-testid="assign-select"><SelectValue placeholder="Pick engineer" /></SelectTrigger>
                      <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" onClick={assign} disabled={!assignEmp} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="do-assign">Assign</Button>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-slate-500 self-center">Status:</span>
                  {STATUSES.map(s => (
                    <Button key={s} size="sm" variant={openTicket.status === s ? "default" : "outline"}
                      className={openTicket.status === s ? "bg-[#DC2626] hover:bg-[#B91C1C]" : ""}
                      onClick={() => setStatus(s)} data-testid={`set-status-${s}`}>{s}</Button>
                  ))}
                </div>

                <div>
                  <div className="text-xs uppercase font-semibold tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                    <MessageSquare className="h-3.5 w-3.5" strokeWidth={1.8} /> Conversation ({openTicket.replies?.length || 0})
                  </div>
                  <div className="space-y-2 mb-3">
                    {(openTicket.replies || []).map(r => (
                      <div key={r.id} className={`p-3 rounded-lg text-sm ${r.role === "employee" ? "bg-slate-50" : "bg-red-50/50 border-l-2 border-[#DC2626]"}`}>
                        <div className="text-xs text-slate-500 mb-1">
                          <b className="text-slate-900">{r.author}</b> · {fmtRel(r.created_at)}
                        </div>
                        <div className="text-slate-800 whitespace-pre-wrap">{r.body}</div>
                      </div>
                    ))}
                    {!(openTicket.replies?.length) && <div className="text-xs text-slate-500 py-4 text-center border border-dashed rounded-lg">No replies yet.</div>}
                  </div>
                  <div className="flex gap-2">
                    <Textarea rows={2} value={reply} onChange={e => setReply(e.target.value)} placeholder="Write a reply…" data-testid="reply-input" />
                    <Button onClick={postReply} disabled={!reply.trim()} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="post-reply">Send</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TicketList({ tickets, showRaiser, onOpen }) {
  if (!tickets?.length) return <div className="text-sm text-slate-500 py-10 text-center border border-dashed rounded-xl">No tickets yet.</div>;
  return (
    <div className="space-y-3">
      {tickets.map(t => {
        const sla = slaState(t);
        return (
          <Card key={t.id} className="p-4 rounded-xl border-slate-200/60 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" onClick={() => onOpen(t)} data-testid={`ticket-${t.ticket_no}`}>
            <div className="flex flex-wrap items-start gap-3 justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-slate-500">{t.ticket_no}</span>
                  <span className="font-semibold text-sm text-slate-900">{t.title}</span>
                  <Badge className={`border-0 text-[10px] ${STATUS_STYLE[t.status]}`}>{t.status}</Badge>
                  <Badge className={`border-0 text-[10px] ${PRIORITY_STYLE[t.priority]}`}>{t.priority}</Badge>
                  <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px]">{t.category}</Badge>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {showRaiser && <><b className="text-slate-700">{t.raised_by_name}</b> · </>}
                  {fmtRel(t.created_at)} · Assigned to <b>{t.assignee_name || "—"}</b>
                </div>
                <div className="text-sm text-slate-600 mt-1 line-clamp-2">{t.description}</div>
              </div>
              <div className={`text-xs flex items-center gap-1 shrink-0 ${sla.tone}`}>
                <Clock className="h-3 w-3" strokeWidth={2} />{sla.label}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  const tones = { red: "text-[#DC2626] bg-red-50", green: "text-emerald-600 bg-emerald-50", orange: "text-orange-600 bg-orange-50" };
  return (
    <Card className="p-5 rounded-xl border-slate-200/60">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{label}</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900">{value}</div>
        </div>
        <div className={`h-10 w-10 rounded-lg grid place-items-center ${tones[tone]}`}><Icon className="h-5 w-5" strokeWidth={1.5} /></div>
      </div>
    </Card>
  );
}

function FormField({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}


function AutoAssignRules({ employees }) {
  const [rules, setRules] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/helpdesk/rules"); setRules(data); setDirty(false); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const setAssignee = (category, empId) => {
    setRules(prev => prev.map(r => r.category === category
      ? { ...r, assignee_id: empId || null, assignee_name: employees.find(e => e.id === empId)?.name || null }
      : r
    ));
    setDirty(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/helpdesk/rules", { rules: rules.map(r => ({ category: r.category, assignee_id: r.assignee_id })) });
      setRules(data); setDirty(false);
      toast.success("Auto-assign rules saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const activeEmployees = employees.filter(e => e.status === "Active");

  return (
    <div className="space-y-4" data-testid="rules-panel">
      <Card className="p-4 rounded-xl border-slate-200/60 bg-blue-50/40 border border-blue-100">
        <div className="text-sm text-slate-700">
          <strong>How it works:</strong> when someone raises a ticket in one of these categories, it will be automatically assigned to the person you pick — no manual triage needed.
          Leave a category blank to keep manual assignment for it.
        </div>
      </Card>

      <div className="grid gap-3">
        {rules.map(r => (
          <Card key={r.category} className="p-4 rounded-xl border-slate-200/60" data-testid={`rule-${r.category}`}>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Category</div>
                <div className="font-semibold text-slate-900 mt-0.5">{r.category}</div>
              </div>
              <div className="text-slate-300 hidden md:block">→</div>
              <div className="flex items-center gap-2 flex-1 min-w-[240px] max-w-md">
                <Select value={r.assignee_id || "__none__"} onValueChange={(v) => setAssignee(r.category, v === "__none__" ? null : v)}>
                  <SelectTrigger className="w-full" data-testid={`rule-assignee-${r.category}`}>
                    <SelectValue placeholder="No auto-assign" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">— No auto-assign —</SelectItem>
                    {activeEmployees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} · {e.designation}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {r.assignee_name && (
                <Badge className="bg-emerald-50 text-emerald-700 border-0 hover:bg-emerald-50">
                  Active · routes to {r.assignee_name}
                </Badge>
              )}
            </div>
          </Card>
        ))}
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        {dirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
        <Button variant="outline" onClick={load} disabled={!dirty || busy}>Reset</Button>
        <Button onClick={save} disabled={!dirty || busy} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="save-rules-btn">
          {busy ? "Saving…" : "Save rules"}
        </Button>
      </div>
    </div>
  );
}
